/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMaterialUIController } from "context";
import { PreviewInvoiceDetails } from "utils/invoiceAPI";
import { downloadInvoice } from "utils/api";
import { savePdfResponse } from "utils/documentPdf";
import { showResMsg } from "utils/generic";
import { appConstants } from "utils/appConstants";
import AppHeader from "components/AppHeader";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import CustomerInvoicePreview from "./CustomerInvoicePreview";
import DocumentPreviewSkeleton from "components/Skeleton/DocumentPreviewSkeleton";
import { resolvePreviewReturn } from "utils/previewReturn";
import "./invoiceFlow.css";
import "../Quotation/quotation.css";

export default function PreviewDownloadInvoices() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: InvoiceID } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const currencyRedux = useSelector((s) => s.currency?.currencySymbol) || "AUD";

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [values, setValues] = useState(null);
  const [uploadedLogoFileName, setUploadedLogoFileName] = useState("");
  const [businessProfile, setBusinessProfile] = useState(null);

  const loginDetails = React.useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
    } catch {
      return {};
    }
  }, []);

  const organisation =
    loginDetails?.companyName ||
    loginDetails?.businessName ||
    loginDetails?.organisationName ||
    loginDetails?.organizationName ||
    "Invoice & Recover";
  const previewReturn = resolvePreviewReturn(
    location,
    `/invoices/view/${InvoiceID}`,
    "Back to details"
  );
  const userName =
    `${loginDetails.firstName || ""} ${loginDetails.lastName || ""}`.trim() ||
    loginDetails.name ||
    "User";
  const role = loginDetails.roleName || loginDetails.userType || "Owner";
  const renderPreviewShell = (content) => (
    <div className={`dashboard-shell quotation-preview-shell${darkMode ? " dark" : ""}`}>
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/invoices"
        organisation={organisation}
        role={role}
      />
      <div className="dashboard-main quotation-preview-main">
        <AppHeader onOpenNav={() => setNavOpen(true)} userName={userName} />
        <main>{content}</main>
      </div>
    </div>
  );

  useEffect(() => {
    fetchInitialData();
  }, [InvoiceID]);

  useEffect(() => {
    if (!loginDetails.businessUserID) return undefined;
    let active = true;
    axios
      .post(`${appConstants.baseURL}defaultSettings/fetchDefaultSetting`, {
        businessUserID: loginDetails.businessUserID,
      })
      .then((response) => {
        if (active) {
          setUploadedLogoFileName(
            response.data?.Result_Output?.defaultLogoFileName || ""
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [loginDetails.businessUserID]);

  useEffect(() => {
    const userId = loginDetails._id || sessionStorage.getItem("USER_ID")?.replace(/["']/g, "");
    if (!userId) return undefined;
    let active = true;
    axios
      .post(
        `${appConstants.baseURL}user/fetchUserProfile`,
        { _id: userId },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` } }
      )
      .then(({ data }) => {
        if (!active) return;
        setBusinessProfile(
          Array.isArray(data?.Result_Output)
            ? data.Result_Output[0] || null
            : data?.Result_Output || null
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [loginDetails._id]);

  const fetchInitialData = async () => {
    setLoading(true);
    PreviewInvoiceDetails(
      { invoiceID: InvoiceID },
      (data) => {
        setLoading(false);
        if (data.Result_Output && data.Result_Output.length > 0) {
          setValues(data.Result_Output[0]);
        } else {
          showResMsg("Invoice details not found", 2000, false);
        }
      },
      dispatch
    );
  };

  const handleDownload = async () => {
    if (!values || !InvoiceID) return;
    setDownloading(true);
    try {
      await downloadInvoice(
        { _id: InvoiceID },
        (response) => {
          savePdfResponse(response, `${values.invoiceNumber || "invoice"}.pdf`);
        },
        dispatch
      );
    } catch (err) {
      console.error(err);
      showResMsg("Unable to download invoice PDF.", 2500, false);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return renderPreviewShell(
      <div className={`invoice-preview-screen-shell${darkMode ? " dark" : ""}`}>
        <DocumentPreviewSkeleton
          variant="invoice"
          label="Loading invoice preview…"
        />
      </div>
    );
  }

  if (!values) {
    return renderPreviewShell(
      <div className={`invoice-preview-screen-shell${darkMode ? " dark" : ""}`}>
        <div style={{ textAlign: "center", padding: "48px" }}>
          <h2>Invoice not found</h2>
          <button
            type="button"
            className="invoice-flow-back"
            onClick={() => navigate(previewReturn.path)}
            style={{ marginTop: "16px" }}
          >
            {previewReturn.label}
          </button>
        </div>
      </div>
    );
  }

  const invoiceItems = values.itemsList || values.items || [];
  const currency = values.currencyValue || currencyRedux || "AUD";
  const paymentsReceived = (values.invoicePayment || []).reduce(
    (sum, payment) =>
      sum + Number(payment.paymentAmount ?? payment.amount ?? 0),
    0
  );
  const logoFileName = values.defaultLogoFileName || uploadedLogoFileName;
  const businessLogo = logoFileName
    ? `${appConstants.baseURL}employee/viewFile?fileName=${encodeURIComponent(
        logoFileName
      )}`
    : "";
  const documentOrganisation =
    values.businessCompanyName || businessProfile?.companyName || organisation;

  return renderPreviewShell(
    <div
      className={`invoice-preview-screen-shell${darkMode ? " dark" : ""}`}
      data-pdf-document-id={InvoiceID}
    >
      <CustomerInvoicePreview
        organisation={documentOrganisation}
        businessLogo={businessLogo}
        invoiceNumber={values.invoiceNumber}
        status={values.invoiceStatus}
        customerName={
          values.customerCompanyName || values.contactName || "Customer"
        }
        customerDetails={{
          emailAddress: values.customerEmailAddress,
          streetAddress: values.customerStreetAddress,
        }}
        invoiceDate={values.invoiceDate}
        dueDate={values.dueDate}
        dueUpto={values.dueUpto || values.validUpto || "14 days"}
        currency={currency}
        gstType={values.gstType}
        items={invoiceItems}
        quotationNumber={values.quotationNumber}
        purchaseOrderNumber={values.purchaseOrderNumber}
        scopeNotes={values.scopeNotes || ""}
        invoiceNotes={values.terms}
        paymentInstructions={values.paymentInstruction}
        bankAccountName={values.bankAccountName || businessProfile?.bankAccountName}
        bankBsbCode={values.bankBsbCode || businessProfile?.bankBsbCode}
        bankAccountNumber={values.bankAccountNumber || businessProfile?.bankAccountNumber}
        subtotal={values.totalAmount}
        totalDiscounts={values.totalDiscountAmount}
        totalGST={values.totalGSTAmount}
        grandTotal={values.finalAmount}
        payablePendingAmount={values.payablePendingAmount}
        paymentsReceived={paymentsReceived}
        isSaved={true}
        topBarVariant="full"
        onBack={() => navigate(previewReturn.path)}
        backLabel={previewReturn.label}
        onDownload={handleDownload}
        isDownloading={downloading}
        darkMode={darkMode}
      />
    </div>
  );
}
